import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Flame, Globe, Users, Heart, Sparkles } from "lucide-react";
import {
    fetchGlobalLeaderboard, fetchClassLeaderboard, fetchJoinedClasses,
    fetchCheerCounts, sendCheer, fetchCheerStreaks,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";

export default function LeaderboardWidget() {
    const { lang } = useLang();
    const { user } = useAuth();
    const [scope, setScope] = useState("global");  // "global" | classId
    const [leaders, setLeaders] = useState(null);
    const [classes, setClasses] = useState([]);
    const [className, setClassName] = useState(null);
    const [cheers, setCheers] = useState({});
    const [streaks, setStreaks] = useState({});

    // Load classes once if authed
    useEffect(() => {
        if (!user) return;
        fetchJoinedClasses().then(setClasses).catch(() => setClasses([]));
    }, [user]);

    useEffect(() => {
        if (scope === "global") {
            setClassName(null);
            fetchGlobalLeaderboard(10).then(setLeaders).catch(() => setLeaders([]));
        } else {
            fetchClassLeaderboard(scope)
                .then((d) => {
                    setLeaders(d.leaders);
                    setClassName(d.class_name);
                })
                .catch(() => setLeaders([]));
        }
    }, [scope]);

    // Load cheer counts whenever leaders change (only for authed users)
    useEffect(() => {
        if (!user || !leaders || leaders.length === 0) {
            setCheers({});
            setStreaks({});
            return;
        }
        const ids = leaders.map((u) => u.user_id);
        fetchCheerCounts(ids).then(setCheers).catch(() => setCheers({}));
        fetchCheerStreaks(ids).then(setStreaks).catch(() => setStreaks({}));
    }, [leaders, user]);

    const cheer = async (uid) => {
        // optimistic
        setCheers((c) => ({
            ...c,
            [uid]: {
                ...(c[uid] || { total: 0, week: 0 }),
                total: (c[uid]?.total || 0) + (c[uid]?.given_by_me ? 0 : 1),
                week: (c[uid]?.week || 0) + (c[uid]?.given_by_me ? 0 : 1),
                given_by_me: true,
            },
        }));
        try {
            await sendCheer(uid);
        } catch (err) {
            console.error("LeaderboardWidget: cheer failed", err);
        }
    };

    if (leaders === null) return null;
    if (scope === "global" && leaders.length === 0) return null;

    return (
        <section
            className="max-w-7xl mx-auto px-5 md:px-8 py-16"
            data-testid="leaderboard-widget"
        >
            <div className="card p-6 md:p-8 relative overflow-hidden">
                <div className="glow glow-lime -top-20 -left-20" />
                <div className="relative z-10">
                    <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
                        <div>
                            <span className="tag mb-2 inline-flex">
                                <Trophy size={12} className="text-[var(--lime)] mr-1" />
                                {lang === "es"
                                    ? "// tabla en vivo"
                                    : "// live leaderboard"}
                            </span>
                            <h2 className="font-display font-black text-white text-3xl md:text-4xl uppercase mt-2">
                                {scope !== "global" && className ? (
                                    <>
                                        {lang === "es" ? "Top de" : "Top of"}{" "}
                                        <span className="font-brush text-[var(--lime)] inline-block -rotate-2">
                                            {className}
                                        </span>
                                    </>
                                ) : lang === "es" ? (
                                    <>
                                        Top del{" "}
                                        <span className="font-brush text-[var(--lime)] inline-block -rotate-2">
                                            barrio
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        Top of the{" "}
                                        <span className="font-brush text-[var(--lime)] inline-block -rotate-2">
                                            block
                                        </span>
                                    </>
                                )}
                            </h2>
                        </div>
                        <Link
                            to="/dashboard"
                            className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--cyan)] hover:text-[var(--lime)]"
                            data-testid="leaderboard-join"
                        >
                            {lang === "es" ? "súmate al rank →" : "join the rank →"}
                        </Link>
                    </div>

                    {/* Scope tabs */}
                    {classes.length > 0 && (
                        <div
                            className="flex flex-wrap gap-2 mb-5"
                            data-testid="leaderboard-scope"
                        >
                            <ScopeBtn
                                active={scope === "global"}
                                onClick={() => setScope("global")}
                                Icon={Globe}
                                label={lang === "es" ? "Global" : "Global"}
                                testid="scope-global"
                            />
                            {classes.map((c) => (
                                <ScopeBtn
                                    key={c.id}
                                    active={scope === c.id}
                                    onClick={() => setScope(c.id)}
                                    Icon={Users}
                                    label={c.name}
                                    testid={`scope-class-${c.id}`}
                                />
                            ))}
                        </div>
                    )}

                    {leaders.length === 0 ? (
                        <p className="font-mono text-[var(--muted)] py-4">
                            {lang === "es"
                                ? "Nadie en el ranking todavía."
                                : "Nobody on the board yet."}
                        </p>
                    ) : (
                        <ol className="space-y-2" data-testid="leaderboard-list">
                            {leaders.slice(0, 10).map((u, i) => {
                                const rank = i + 1;
                                const medal =
                                    rank === 1
                                        ? "🥇"
                                        : rank === 2
                                          ? "🥈"
                                          : rank === 3
                                            ? "🥉"
                                            : `#${rank}`;
                                const accent =
                                    rank === 1
                                        ? "var(--lime)"
                                        : rank <= 3
                                          ? "var(--cyan)"
                                          : "var(--muted)";
                                const isMe = u.user_id === user?.user_id;
                                return (
                                    <li
                                        key={u.user_id}
                                        className={
                                            "flex items-center gap-4 border p-3 " +
                                            (rank <= 3
                                                ? "border-[var(--lime)]/40 bg-[var(--lime)]/[0.04]"
                                                : isMe
                                                  ? "border-[var(--cyan)] bg-[var(--cyan)]/[0.05]"
                                                  : "border-[var(--border)]")
                                        }
                                        data-testid={`leaderboard-row-${rank}`}
                                    >
                                        <span
                                            className="font-display font-black text-2xl w-10 text-center"
                                            style={{ color: accent }}
                                        >
                                            {medal}
                                        </span>
                                        <span className="font-mono text-white flex-1 truncate flex items-center gap-2">
                                            <span className="truncate">
                                                {u.name || "anonymous"}
                                            </span>
                                            {isMe && (
                                                <span className="text-[var(--cyan)] text-xs">
                                                    ← you
                                                </span>
                                            )}
                                            {(streaks[u.user_id] || 0) >= 2 && (
                                                <span
                                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-[var(--lime)] text-[var(--lime)] font-mono text-[10px] uppercase tracking-widest"
                                                    title={
                                                        lang === "es"
                                                            ? `Animado ${streaks[u.user_id]} semanas seguidas`
                                                            : `Cheered ${streaks[u.user_id]} weeks in a row`
                                                    }
                                                    data-testid={`cheer-streak-${u.user_id}`}
                                                >
                                                    <Sparkles size={10} />
                                                    {streaks[u.user_id]}
                                                    {lang === "es" ? "sem" : "wk"}
                                                </span>
                                            )}
                                        </span>
                                        <span className="font-mono text-xs text-[var(--muted)] flex items-center gap-3">
                                            <span className="flex items-center gap-1 text-[var(--coral)]">
                                                <Flame size={12} />
                                                {u.streak}
                                            </span>
                                            <span>{u.completed_weeks}/6</span>
                                            <span className="text-[var(--lime)] font-bold">
                                                {u.total_score} pts
                                            </span>
                                            {user && !isMe && (
                                                <button
                                                    type="button"
                                                    onClick={() => cheer(u.user_id)}
                                                    data-testid={`cheer-${u.user_id}`}
                                                    disabled={cheers[u.user_id]?.given_by_me}
                                                    title={
                                                        cheers[u.user_id]?.given_by_me
                                                            ? "Already cheered this week"
                                                            : "Send a cheer"
                                                    }
                                                    className={
                                                        "flex items-center gap-1 px-2 py-1 border transition-colors " +
                                                        (cheers[u.user_id]?.given_by_me
                                                            ? "border-[var(--coral)] text-[var(--coral)] bg-[var(--coral)]/10"
                                                            : "border-[var(--border)] text-[var(--muted)] hover:border-[var(--coral)] hover:text-[var(--coral)]")
                                                    }
                                                >
                                                    <Heart
                                                        size={11}
                                                        fill={
                                                            cheers[u.user_id]?.given_by_me
                                                                ? "var(--coral)"
                                                                : "none"
                                                        }
                                                    />
                                                    <span>{cheers[u.user_id]?.total || 0}</span>
                                                </button>
                                            )}
                                            {(!user || isMe) &&
                                                (cheers[u.user_id]?.total || 0) > 0 && (
                                                    <span className="flex items-center gap-1 text-[var(--coral)]">
                                                        <Heart size={11} fill="var(--coral)" />
                                                        {cheers[u.user_id].total}
                                                    </span>
                                                )}
                                        </span>
                                    </li>
                                );
                            })}
                        </ol>
                    )}
                    {scope === "global" && (
                        <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] mt-4">
                            {lang === "es"
                                ? "solo aparecen estudiantes que activan el opt-in en su panel."
                                : "only students who opt-in from their dashboard appear here."}
                        </p>
                    )}
                </div>
            </div>
        </section>
    );
}

function ScopeBtn({ active, onClick, Icon, label, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={
                "font-mono text-[10px] uppercase tracking-[0.18em] px-3 py-2 border flex items-center gap-1.5 transition-colors " +
                (active
                    ? "border-[var(--lime)] text-[var(--lime)] bg-[var(--lime)]/5"
                    : "border-[var(--border)] text-[var(--muted)] hover:text-white")
            }
        >
            <Icon size={11} />
            {label}
        </button>
    );
}
